import { NAMESPACE } from './constants';
import { authenticatedLogger } from './authenticatedLogger';
import { GlobalDimensions } from './metricsConfig';
import { METRIC_NAME, METRIC_UNIT } from './types';

class MetricsPublisher {
  private globalDimensions: GlobalDimensions;

  constructor() {
    this.globalDimensions = GlobalDimensions.getInstance();
  }

  private async publishMetric(
    name: string,
    value: number,
    unit: string,
    dimensions?: Record<string, string>,
  ) {
    const allDimensions = this.globalDimensions.getGlobalDimensions();

    const emfMetric = {
      _aws: {
        Timestamp: Date.now(),
        CloudWatchMetrics: [
          {
            Namespace: NAMESPACE,
            Dimensions: [Object.keys({ ...allDimensions, ...(dimensions || {}) })],
            Metrics: [{ Name: name, Unit: unit }],
          },
        ],
      },
      ...allDimensions,
      ...dimensions,
      [name]: value,
    };

    try {
      (await authenticatedLogger.getLogger()).info('Publishing a metric', { emfLog: emfMetric });
    } catch (error) {
      console.warn('Failed to publish metric:', name, error);
    }
  }

  public async emitCount(metricName: METRIC_NAME, dimensions?: Record<string, string>) {
    await this.publishMetric(metricName, 1, METRIC_UNIT.COUNT, dimensions);
  }

  public async emitBool(
    metricName: METRIC_NAME,
    value: true | false,
    dimensions?: Record<string, string>,
  ) {
    await this.publishMetric(metricName, value ? 1 : 0, METRIC_UNIT.COUNT, dimensions);
  }

  public async emitLatency(
    metricName: METRIC_NAME,
    durationMs: number,
    dimensions?: Record<string, string>,
  ) {
    await this.publishMetric(metricName, durationMs, METRIC_UNIT.MILLISECONDS, dimensions);
  }

  public async trackLatency<T>(
    metricName: METRIC_NAME,
    fn: () => Promise<T>,
    dimensions?: Record<string, string>,
  ): Promise<T> {
    const startTime = performance.now();
    try {
      return await fn();
    } finally {
      void this.emitLatency(metricName, performance.now() - startTime, dimensions).catch(() => {});
    }
  }

  public addGlobalDimensions(dimensions: Record<string, string>) {
    this.globalDimensions.addGlobalDimensions(dimensions);
  }
}

export const metricsPublisher = new MetricsPublisher();
